import React, { useEffect, useState, useRef, useCallback } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Upload, Trash2, Download, FolderLock, Loader2, Eye, Folder, FolderPlus, FolderInput, Pencil, ArrowLeft,
  File as FileIcon, FileText, FileVideo, FileAudio, FileArchive, FileSpreadsheet, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

const CHUNK = 4 * 1024 * 1024;

// Preview allow-list — extension-keyed safe MIME types only (no SVG/HTML:
// those can carry scripts, so they stay download-only).
const SAFE_PREVIEW_MIME = {
  png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif",
  webp: "image/webp", bmp: "image/bmp", pdf: "application/pdf",
};
const previewKind = (f) => {
  const ext = (f.filename || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return "pdf";
  return SAFE_PREVIEW_MIME[ext] ? "image" : null;
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

const iconFor = (f) => {
  const ct = f.content_type || "";
  const ext = (f.filename || "").split(".").pop().toLowerCase();
  if (ct.startsWith("video/") || ["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return FileVideo;
  if (ct.startsWith("audio/") || ["mp3", "wav", "m4a", "flac"].includes(ext)) return FileAudio;
  if (ct.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "heic"].includes(ext)) return ImageIcon;
  if (["zip", "rar", "7z", "gz", "tar"].includes(ext)) return FileArchive;
  if (["xls", "xlsx", "xlsm", "csv"].includes(ext)) return FileSpreadsheet;
  if (["pdf", "doc", "docx", "txt", "md"].includes(ext)) return FileText;
  return FileIcon;
};

export default function MyFiles() {
  const [data, setData] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const [folder, setFolder] = useState(null); // currently open folder (object) or null = root
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const inputRef = useRef(null);
  const dragDepth = useRef(0);
  const folderRef = useRef(null);
  folderRef.current = folder;

  const refresh = useCallback(() => {
    api.get("/myfiles").then((r) => setData(r.data)).catch(() => setData({ files: [], folders: [], used_bytes: 0, quota_bytes: 5 * 1024 ** 3 }));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const uploadOne = async (file) => {
    const key = `${file.name}-${Date.now()}-${Math.random()}`;
    const targetFolder = folderRef.current;
    setUploads((u) => [...u, { key, name: file.name, pct: 0 }]);
    const setPct = (pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x)));
    try {
      const { data: s } = await api.post("/myfiles/upload/start", {
        filename: file.name,
        size: file.size,
        content_type: file.type || "application/octet-stream",
        folder_id: targetFolder?.id || null,
      });
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
        await api.post(`/myfiles/upload/chunk?upload_id=${s.upload_id}&index=${i}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        setPct(Math.round(((i + 1) / (total + 1)) * 100));
      }
      await api.post("/myfiles/upload/complete", { upload_id: s.upload_id });
      setPct(100);
      toast.success(`${file.name} uploaded${targetFolder ? ` to ${targetFolder.name}` : ""}`);
      setTimeout(() => setUploads((u) => u.filter((x) => x.key !== key)), 1200);
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      toast.error(`${file.name}: ${msg}`);
      setUploads((u) => u.filter((x) => x.key !== key));
    }
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    files.forEach(uploadOne);
  };

  const download = async (f) => {
    try {
      toast.info(`Downloading ${f.filename}…`);
      const resp = await fetch(`${API_BASE}/myfiles/${f.id}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/myfiles/${f.id}`);
      toast.success("File deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const openPreview = async (f) => {
    const kind = previewKind(f);
    if (!kind) return download(f);
    setPreviewLoading(f.id);
    try {
      const resp = await fetch(`${API_BASE}/myfiles/${f.id}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error();
      const raw = await resp.blob();
      const ext = f.filename.split(".").pop().toLowerCase();
      const blob = new Blob([raw], { type: SAFE_PREVIEW_MIME[ext] });
      setPreview({ name: f.filename, url: URL.createObjectURL(blob), kind, file: f });
    } catch {
      toast.error("Preview failed");
    } finally {
      setPreviewLoading(null);
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const createFolder = async () => {
    try {
      const r = await api.post("/myfiles/folders", { name: folderName });
      toast.success(`Folder "${r.data.name}" created`);
      setNewFolderOpen(false);
      setFolderName("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create folder");
    }
  };

  const renameFolder = async () => {
    try {
      await api.patch(`/myfiles/folders/${renameTarget.id}`, { name: folderName });
      toast.success("Folder renamed");
      if (folder?.id === renameTarget.id) setFolder({ ...folder, name: folderName });
      setRenameTarget(null);
      setFolderName("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename folder");
    }
  };

  const deleteFolder = async (fo) => {
    const count = (data?.files || []).filter((f) => f.folder_id === fo.id).length;
    if (!window.confirm(`Delete folder "${fo.name}"?${count ? ` Its ${count} file${count === 1 ? "" : "s"} will move back to My Files.` : ""}`)) return;
    try {
      await api.delete(`/myfiles/folders/${fo.id}`);
      toast.success("Folder deleted");
      if (folder?.id === fo.id) setFolder(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete folder");
    }
  };

  const moveFile = async (targetFolderId) => {
    try {
      await api.patch(`/myfiles/${moveTarget.id}`, { folder_id: targetFolderId });
      const dest = targetFolderId ? (data.folders.find((x) => x.id === targetFolderId)?.name || "folder") : "My Files";
      toast.success(`Moved to ${dest}`);
      setMoveTarget(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move file");
    }
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    dragDepth.current += 1;
    if (e.dataTransfer?.types?.includes("Files")) setDragging(true);
  };
  const onDragOver = (e) => e.preventDefault();
  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    Array.from(e.dataTransfer?.files || []).forEach(uploadOne);
  };

  if (!data) return null;
  const used = data.used_bytes || 0;
  const quota = data.quota_bytes || 5 * 1024 ** 3;
  const pct = Math.min(100, Math.round((used / quota) * 100));
  const barCls = pct >= 95 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-summit-primary";
  const folders = data.folders || [];
  const visibleFiles = (data.files || []).filter((f) => (folder ? f.folder_id === folder.id : !f.folder_id));
  const countIn = (fid) => (data.files || []).filter((f) => f.folder_id === fid).length;

  return (
    <div
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 min-h-[calc(100vh-5rem)]"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="myfiles-page"
    >
      {dragging && (
        <div className="fixed inset-0 z-50 bg-summit-primary/10 backdrop-blur-[2px] m-3 rounded-2xl border-4 border-dashed border-summit-primary flex items-center justify-center pointer-events-none" data-testid="myfiles-dropzone-overlay">
          <div className="bg-white rounded-xl shadow-xl px-8 py-6 text-center">
            <Upload className="w-8 h-8 mx-auto text-summit-primary" />
            <p className="font-medium mt-2">Drop files to upload{folder ? ` into ${folder.name}` : ""}</p>
            <p className="text-xs text-gray-500 mt-0.5">Any format · 5 GB total</p>
          </div>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <FolderLock className="w-3 h-3" /> Private storage
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">My Files</h1>
          <p className="text-sm text-gray-500 mt-2">Your personal file locker — any format, up to 5 GB. Drag &amp; drop anywhere on this page to upload. Only you can see these files.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setFolderName(""); setNewFolderOpen(true); }}
            data-testid="myfiles-new-folder-btn"
          >
            <FolderPlus className="w-4 h-4 mr-1.5" /> New folder
          </Button>
          <Button
            className="bg-summit-primary hover:bg-summit-primary-soft text-white"
            onClick={() => inputRef.current?.click()}
            data-testid="myfiles-upload-btn"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Upload files
          </Button>
        </div>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={onPick} data-testid="myfiles-file-input" />
      </div>

      <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-4" data-testid="myfiles-usage">
        <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest text-gray-500">
          <span>Storage used</span>
          <span data-testid="myfiles-usage-label"><span className="font-bold text-gray-800">{fmtBytes(used)}</span> / {fmtBytes(quota)}</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${barCls}`} style={{ width: `${Math.max(pct, used > 0 ? 2 : 0)}%` }} />
        </div>
      </div>

      {uploads.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="myfiles-uploading">
          {uploads.map((u) => (
            <div key={u.key} className="border border-summit-hairline rounded-lg bg-white px-4 py-2.5 flex items-center gap-3">
              <Loader2 className="w-4 h-4 animate-spin text-summit-primary shrink-0" />
              <span className="text-sm truncate flex-1">{u.name}</span>
              <div className="w-32 h-1.5 rounded-full bg-gray-100 overflow-hidden shrink-0">
                <div className="h-full bg-summit-primary rounded-full transition-all" style={{ width: `${u.pct}%` }} />
              </div>
              <span className="font-mono text-[10px] text-gray-500 w-9 text-right shrink-0">{u.pct}%</span>
            </div>
          ))}
        </div>
      )}

      {folder && (
        <div className="mt-4 flex items-center gap-2" data-testid="myfiles-breadcrumb">
          <button
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-summit-primary hover:underline"
            onClick={() => setFolder(null)}
            data-testid="myfiles-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> My Files
          </button>
          <span className="text-gray-300">/</span>
          <span className="flex items-center gap-1.5 font-medium text-sm">
            <Folder className="w-4 h-4 text-amber-500" /> {folder.name}
          </span>
        </div>
      )}

      <div className="mt-4 border border-summit-hairline rounded-lg bg-white overflow-hidden">
        {!folder && folders.length > 0 && (
          <div className="border-b border-summit-hairline">
            {folders.map((fo) => (
              <div key={fo.id} className="flex items-center gap-2.5 px-4 py-2.5 border-b border-summit-hairline last:border-0 hover:bg-summit-glacier-soft/60" data-testid={`myfiles-folder-row-${fo.id}`}>
                <Folder className="w-4 h-4 text-amber-500 shrink-0" />
                <button
                  className="font-medium text-left truncate flex-1 hover:text-summit-primary hover:underline"
                  onClick={() => setFolder(fo)}
                  data-testid={`myfiles-folder-open-${fo.id}`}
                >
                  {fo.name}
                </button>
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 shrink-0">
                  {countIn(fo.id)} file{countIn(fo.id) === 1 ? "" : "s"}
                </span>
                <button className="p-1.5 rounded hover:bg-summit-glacier text-gray-500" onClick={() => { setRenameTarget(fo); setFolderName(fo.name); }} title="Rename folder" data-testid={`myfiles-folder-rename-${fo.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button className="p-1.5 rounded hover:bg-rose-50 text-rose-500" onClick={() => deleteFolder(fo)} title="Delete folder" data-testid={`myfiles-folder-delete-${fo.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        {visibleFiles.length === 0 ? (
          <div className="py-14 text-center text-gray-400" data-testid="myfiles-empty">
            <FolderLock className="w-8 h-8 mx-auto text-gray-300" />
            <p className="text-sm mt-3">{folder ? "This folder is empty — upload or move files into it." : "No files yet — upload documents, photos or videos to keep them handy."}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-summit-canvas border-b border-summit-hairline">
                <th className="text-left px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">File</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">Size</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 hidden sm:table-cell">Uploaded</th>
                <th className="px-3 py-2 w-32" />
              </tr>
            </thead>
            <tbody>
              {visibleFiles.map((f) => {
                const Icon = iconFor(f);
                const canPreview = !!previewKind(f);
                return (
                  <tr key={f.id} className="border-b border-summit-hairline last:border-0 hover:bg-summit-glacier-soft/60" data-testid={`myfiles-row-${f.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Icon className="w-4 h-4 text-summit-primary-soft shrink-0" />
                        <button
                          className={`truncate font-medium text-left ${canPreview ? "hover:text-summit-primary hover:underline" : "cursor-default"}`}
                          title={canPreview ? `Preview ${f.filename}` : f.filename}
                          onClick={() => canPreview && openPreview(f)}
                          data-testid={`myfiles-name-${f.id}`}
                        >
                          {f.filename}
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-gray-600 whitespace-nowrap">{fmtBytes(f.size)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-[11px] text-gray-500 whitespace-nowrap hidden sm:table-cell">{(f.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {canPreview && (
                          <button className="p-1.5 rounded hover:bg-summit-glacier text-gray-600" onClick={() => openPreview(f)} title="Preview" data-testid={`myfiles-preview-${f.id}`}>
                            {previewLoading === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                        <button className="p-1.5 rounded hover:bg-summit-glacier text-gray-600" onClick={() => setMoveTarget(f)} title="Move to folder" data-testid={`myfiles-move-${f.id}`}>
                          <FolderInput className="w-4 h-4" />
                        </button>
                        <button className="p-1.5 rounded hover:bg-summit-glacier text-summit-primary" onClick={() => download(f)} title="Download" data-testid={`myfiles-download-${f.id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                        <button className="p-1.5 rounded hover:bg-rose-50 text-rose-500" onClick={() => remove(f)} title="Delete" data-testid={`myfiles-delete-${f.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* New / rename folder dialog */}
      <Dialog open={newFolderOpen || !!renameTarget} onOpenChange={(o) => { if (!o) { setNewFolderOpen(false); setRenameTarget(null); setFolderName(""); } }}>
        <DialogContent className="max-w-sm" data-testid="myfiles-folder-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{renameTarget ? "Rename folder" : "New folder"}</DialogTitle>
          </DialogHeader>
          <Input
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="e.g. Certificates"
            maxLength={60}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && folderName.trim()) (renameTarget ? renameFolder : createFolder)(); }}
            data-testid="myfiles-folder-name-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setNewFolderOpen(false); setRenameTarget(null); setFolderName(""); }}>Cancel</Button>
            <Button
              size="sm"
              className="bg-summit-primary hover:bg-summit-primary-soft text-white"
              disabled={!folderName.trim()}
              onClick={() => (renameTarget ? renameFolder() : createFolder())}
              data-testid="myfiles-folder-save-btn"
            >
              {renameTarget ? "Rename" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move file dialog */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => { if (!o) setMoveTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="myfiles-move-dialog">
          <DialogHeader>
            <DialogTitle className="text-base truncate">Move "{moveTarget?.filename}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {moveTarget?.folder_id && (
              <button
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-summit-glacier text-left text-sm"
                onClick={() => moveFile(null)}
                data-testid="myfiles-move-to-root"
              >
                <FolderLock className="w-4 h-4 text-summit-primary" /> My Files (no folder)
              </button>
            )}
            {folders.filter((fo) => fo.id !== moveTarget?.folder_id).map((fo) => (
              <button
                key={fo.id}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-summit-glacier text-left text-sm"
                onClick={() => moveFile(fo.id)}
                data-testid={`myfiles-move-to-${fo.id}`}
              >
                <Folder className="w-4 h-4 text-amber-500" /> {fo.name}
              </button>
            ))}
            {folders.filter((fo) => fo.id !== moveTarget?.folder_id).length === 0 && !moveTarget?.folder_id && (
              <p className="text-sm text-gray-500 px-3 py-2">No folders yet — create one first with "New folder".</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden gap-0" data-testid="myfiles-preview-dialog">
          <DialogHeader className="px-4 py-3 border-b border-summit-hairline">
            <DialogTitle className="text-sm font-medium truncate pr-8">{preview?.name}</DialogTitle>
          </DialogHeader>
          <div className="bg-gray-900 flex items-center justify-center" style={{ height: "70vh" }}>
            {preview?.kind === "image" ? (
              <img src={preview.url} alt={preview.name} className="max-h-full max-w-full object-contain" data-testid="myfiles-preview-image" />
            ) : preview ? (
              <iframe title={preview.name} src={preview.url} className="w-full h-full bg-white border-0" data-testid="myfiles-preview-pdf" />
            ) : null}
          </div>
          <div className="px-4 py-2.5 flex justify-end border-t border-summit-hairline">
            <Button variant="outline" size="sm" onClick={() => preview && download(preview.file)} data-testid="myfiles-preview-download-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
